'use client';

import Link from 'next/link';
import { useTranslation } from '@/context/LanguageContext';
import { NewsItem, NewsImage } from '@/types/news';
import NewsGallery from '@/components/news/NewsGallery';
import ShareSection from '@/components/shared/ShareSection';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faHome, faChevronRight, faTags, faArrowLeft
} from '@fortawesome/free-solid-svg-icons';
import { faCalendar, faBuilding, faUser } from '@fortawesome/free-regular-svg-icons';

interface NewsDetailContentProps {
    news: NewsItem;
    images: NewsImage[];
    type: 'berita' | 'pengumuman';
}

export default function NewsDetailContent({ news, images, type }: NewsDetailContentProps) {
    const { language } = useTranslation();
    const siteName = process.env.NEXT_PUBLIC_SITE_NAME || 'Universitas Mulawarman';
    const siteNameEnglish = process.env.NEXT_PUBLIC_SITE_NAME_ENGLISH || 'Mulawarman University';

    const { title_idn, title_en, id, published_date, tags, content_idn, content_en, unit_name, slug, filename } = news;

    // Dynamic Content Selection based on Language
    const title = language === 'en' ? (title_en || title_idn) : (title_idn || title_en);
    const content = language === 'en' ? (content_en || content_idn) : (content_idn || content_en);
    const defaultTitle = type === 'berita' ? (language === 'en' ? 'News' : 'Berita') : (language === 'en' ? 'Announcement' : 'Pengumuman');
    const displayTitle = title || defaultTitle;

    const imageUrl = filename
        ? `https://unmul.ac.id/uploads/posts/${filename}`
        : `/api/doServicesProxy?endpoint=web/file/news/${id}&isImage=true`;

    const formatDate = (dateString: string) => {
        const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
        // Use 'id-ID' or 'en-US' based on language
        return new Date(dateString).toLocaleDateString(language === 'en' ? 'en-US' : 'id-ID', options);
    };

    const listPath = type === 'berita' ? '/berita' : '/pengumuman';
    const listLabel = type === 'berita' ? (language === 'en' ? 'News' : 'Berita') : (language === 'en' ? 'Announcement' : 'Pengumuman');
    const detailLabel = language === 'en' ? 'Detail' : 'Detail';
    const shareTitle = language === 'en' ? `Share This ${type === 'berita' ? 'News' : 'Announcement'}` : `Bagikan ${type === 'berita' ? 'Berita' : 'Pengumuman'} Ini`;
    const shareDesc = language === 'en' ? 'Share this information with your friends and colleagues' : 'Sebarkan informasi ke teman dan kolega Anda';
    const backLabel = language === 'en' ? `See Other ${type === 'berita' ? 'News' : 'Announcements'}` : `Lihat ${type === 'berita' ? 'Berita' : 'Pengumuman'} Lainnya`;

    return (
        <>
            {/* Hero Header Section - Creative Theme Colored */}
            <section className="pt-36 pb-16 bg-unmulGreen relative overflow-hidden">
                {/* SVG Ornaments Background */}
                <div className="absolute inset-0 opacity-10">
                    <svg className="absolute w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                        <path d="M0 100 C 20 0 50 0 100 100 Z" fill="white" />
                    </svg>
                    {/* Dots Pattern */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-[url('/img/pattern-dots.svg')] opacity-20 transform rotate-45"></div>
                    {/* Circular Blurs */}
                    <div className="absolute top-10 right-10 w-48 h-48 bg-unmulYellow rounded-full blur-3xl opacity-30"></div>
                    <div className="absolute bottom-10 left-10 w-64 h-64 bg-white rounded-full blur-3xl opacity-20"></div>
                </div>

                <div className="container mx-auto px-6 relative z-10">
                    {/* Breadcrumbs */}
                    <nav className="flex items-center gap-2 text-sm text-white/80 mb-8">
                        <Link href="/" className="hover:text-white transition">
                            <FontAwesomeIcon icon={faHome} />
                        </Link>
                        <FontAwesomeIcon icon={faChevronRight} className="text-xs text-white/50" />
                        <Link href={listPath} className="hover:text-white transition capitalize">
                            {listLabel}
                        </Link>
                        <FontAwesomeIcon icon={faChevronRight} className="text-xs text-white/50" />
                        <span className="text-unmulYellow font-medium">{detailLabel}</span>
                    </nav>



                    {/* Title */}
                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-white leading-tight max-w-5xl">
                        {displayTitle}
                    </h1>

                    {/* Meta Info */}
                    <div className="flex flex-wrap items-center gap-6 mt-8 text-white/90">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                                <FontAwesomeIcon icon={faCalendar} className="text-sm" />
                            </div>
                            <span className="font-medium">{formatDate(published_date)}</span>
                        </div>
                        {unit_name && (
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                                    <FontAwesomeIcon icon={faBuilding} className="text-sm" />
                                </div>
                                <span className="font-medium">{unit_name}</span>
                            </div>
                        )}
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                                <FontAwesomeIcon icon={faUser} className="text-sm" />
                            </div>
                            <span className="font-medium">Admin {language === 'en' ? siteNameEnglish : siteName}</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Article Content Section */}
            <article className="py-12 bg-white">
                <div className="container mx-auto px-6">
                    {/* Featured Image or Gallery - Full Width */}
                    <div className="max-w-6xl mx-auto mb-10">
                        <NewsGallery
                            initialImage={imageUrl}
                            galleryImages={images}
                        />
                    </div>

                    {/* Article Content - Wide */}
                    <div className="max-w-6xl mx-auto">
                        <div
                            className={`prose prose-lg lg:prose-xl max-w-none text-gray-800
                        prose-headings:font-serif
                        prose-a:text-unmulGreen prose-a:no-underline hover:prose-a:underline
                        prose-img:rounded-xl prose-img:shadow-lg prose-img:mx-auto
                        prose-blockquote:border-l-unmulGreen prose-blockquote:bg-gray-50 prose-blockquote:py-2 prose-blockquote:px-4 prose-blockquote:rounded-r-lg
                        
                        /* Explicit List Styling */
                        [&_ol]:list-decimal [&_ul]:list-disc
                        [&_ol]:pl-10 [&_ul]:pl-10
                        [&_li]:pl-2 [&_li]:mb-2
                        [&_li::marker]:text-unmulGreen [&_li::marker]:font-bold
                        
                        /* WYSIWYG Fixes */
                        [&_table]:w-full [&_table]:border-collapse [&_table]:my-6
                        [&_td]:border [&_td]:border-gray-200 [&_td]:p-3 [&_td]:align-top
                        [&_th]:border [&_th]:border-gray-200 [&_th]:p-3 [&_th]:bg-gray-50 [&_th]:text-left [&_th]:font-bold
                        [&_iframe]:w-full [&_iframe]:aspect-video [&_iframe]:rounded-xl [&_iframe]:shadow-md
                        [&_figure]:mx-auto [&_figure]:text-center
                        [&_figcaption]:text-center [&_figcaption]:text-sm [&_figcaption]:text-gray-500 [&_figcaption]:mt-2
                        break-words [&_p:empty]:min-h-[1em]`}
                            dangerouslySetInnerHTML={{ __html: content || '' }}
                        />

                        {/* Divider */}
                        <hr className="my-10 border-gray-200" />

                        {/* Tags */}
                        {tags && tags.length > 0 && (
                            <div className="flex flex-wrap items-center gap-2 mb-8">
                                <span className="text-gray-500 text-sm font-medium mr-2">
                                    <FontAwesomeIcon icon={faTags} className="mr-1" /> Tags:
                                </span>
                                {tags.map((tag, idx) => (
                                    <Link
                                        key={idx}
                                        href={`${listPath}?tag=${tag.title}`}
                                        className="px-4 py-1.5 bg-gray-100 text-gray-600 rounded-full text-sm font-medium hover:bg-unmulGreen hover:text-white transition"
                                    >
                                        {tag.title}
                                    </Link>
                                ))}
                            </div>
                        )}

                        {/* Share Section */}
                        <ShareSection
                            title={displayTitle}
                            url={`${process.env.NEXT_PUBLIC_ORIGIN || ''}${listPath}/${slug}`}
                            shareTitle={shareTitle}
                            shareDesc={shareDesc}
                        />

                        {/* Navigation */}
                        <div className="mt-10 flex justify-center">
                            <Link
                                href={listPath}
                                className="inline-flex items-center gap-2 text-unmulGreen font-semibold hover:gap-3 transition-all"
                            >
                                <FontAwesomeIcon icon={faArrowLeft} />
                                {backLabel}
                            </Link>
                        </div>
                    </div>
                </div>
            </article>
        </>
    );
}
